import { useEffect, useState } from 'react';

import { getAgentSettings } from '@sbrc/services/feathers-client/agent-settings';
import { useRobotIdentity } from '@sbrc/services/feathers-client/robot-identity';

import type { ChatWarning } from './ChatWarnings';

const POSE_SIMULATION_WARNING: ChatWarning = {
  id: 'pose-simulation-unknown-mode',
  message:
    'This robot’s operation mode is unknown, so the agent will fall back to simulation mode and invent placeholder positions for anything you haven’t taught. Check them before playing the routine on hardware.',
};

/**
 * Warns that the agent will build with simulated poses because the robot's
 * operation mode could not be determined.
 *
 * With pose simulation on, the agent's prompt treats an `unknown` operation
 * mode exactly like `simulated`: it invents joint angles for untaught
 * positions and never asks first. That is the right default for an
 * unprovisioned bench robot, but `unknown` is also what a box reports when its
 * identity is unreadable — so the operator is told which branch the agent took
 * rather than discovering invented poses in a finished routine.
 *
 * Null in every other case: any known operation mode, pose simulation off, or
 * the settings read failing (a warning that can't confirm the setting would be
 * guessing).
 */
export function usePoseSimulationWarning(enabled = true): ChatWarning | null {
  const robotIdentity = useRobotIdentity();
  const [isPoseSimulationEnabled, setIsPoseSimulationEnabled] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;

    // Guard against a late response landing after unmount. A failed read
    // leaves the flag false, so the warning stays hidden rather than warning
    // about a setting we could not confirm.
    let cancelled = false;

    getAgentSettings()
      .then(({ poseSimulationMode }) => {
        if (cancelled) return;
        setIsPoseSimulationEnabled(poseSimulationMode);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // `null` is the pre-subscription state, not a robot reporting nothing, so it
  // must not trigger the warning on every mount.
  const operationMode = robotIdentity?.robotOperationMode;

  if (!isPoseSimulationEnabled || operationMode !== 'unknown') return null;

  return POSE_SIMULATION_WARNING;
}
